/**
 * Page picker for list-view bulk sends.
 *
 * When the widget is opened from a CRM list view with "select all" (no ticked
 * ids), the selection can cover far more records than one sending session
 * should handle. One page of records is loaded per session; this row lets the
 * user choose which page to pull in, and picks up where the last session left
 * off instead of starting from page 1 again.
 *
 * Lives inside the recipients ("To") chip box, under the chips, so the control
 * that fills the box sits in the box it fills — and stays put whether or not
 * records are loaded.
 *
 * Only renders for SaaS modules that expose list-view paging.
 */

import { useEffect, useState } from 'react';
import { ChevronRight, Loader2, Layers } from 'lucide-react';
import { useModuleStore } from '@/stores/module-store';
import { useMessageStore } from '@/stores/message-store';

export function ListPagePicker() {
  const saasModule = useModuleStore((s) => s.saasModule);
  const setCrmRecords = useModuleStore((s) => s.setCrmRecords);
  const recipients = useMessageStore((s) => s.recipients);
  const setRecipients = useMessageStore((s) => s.setRecipients);

  const paging = saasModule?.getListViewPaging?.() ?? null;

  const [pageInput, setPageInput] = useState<string>(String(paging?.page ?? 1));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mirrors the module's paging state, which is not reactive on its own.
  const [current, setCurrent] = useState(paging);

  // Adopt the module's page once the list-view context finishes resolving
  // (it lands after this component first mounts).
  useEffect(() => {
    const resolved = saasModule?.getListViewPaging?.() ?? null;
    if (resolved && resolved.page !== current?.page) {
      setCurrent(resolved);
      setPageInput(String(resolved.page));
    }
    // `recipients.length` is the cheapest signal that the initial load landed.
  }, [saasModule, recipients.length, current?.page]);

  if (!current) return null;

  const { page, pageSize, hasMore, lastSentPage, module } = current;
  const firstRecord = (page - 1) * pageSize + 1;
  const lastRecord = page * pageSize;

  const loadPage = async (target: number) => {
    if (!saasModule?.loadListViewPage || loading) return;
    setLoading(true);
    setError(null);
    try {
      const records = await saasModule.loadListViewPage(target);
      setCrmRecords(records);

      // Replace CRM recipients only — anything added manually or from CSV stays.
      const fresh = saasModule.addRecordsAsRecipients(records);
      const nonCrm = useMessageStore.getState().recipients.filter((r) => r.source !== 'crm');
      setRecipients([...nonCrm, ...fresh]);

      setCurrent(saasModule.getListViewPaging?.() ?? null);
      if (records.length === 0) {
        setError(`No records on page ${target}.`);
      } else if (fresh.length === 0) {
        setError(`Page ${target} has ${records.length} records but none with a phone number.`);
      }
    } catch {
      setError('Could not load that page.');
    } finally {
      setLoading(false);
    }
  };

  const submitPage = () => {
    const parsed = Number.parseInt(pageInput, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setPageInput(String(page));
      setError('Enter a page number of 1 or more.');
      return;
    }
    loadPage(parsed);
  };

  return (
    <div
      data-testid="list-page-picker"
      // The chip box focuses its text input on any click — don't let that
      // steal focus from the page field.
      onClick={(e) => e.stopPropagation()}
      className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1.5 pt-1.5 border-t border-border/40 cursor-default"
    >
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Layers className="h-3 w-3" />
        Page
      </span>

      {/* Same dashed-box treatment as the country-code field in the row above. */}
      <div className="inline-flex items-center rounded border border-dashed border-input px-1.5 py-0.5 focus-within:border-solid focus-within:border-primary">
        <input
          data-testid="list-page-input"
          inputMode="numeric"
          value={pageInput}
          disabled={loading}
          onChange={(e) => setPageInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitPage();
            }
          }}
          className="w-7 bg-transparent text-center text-[11px] font-medium tabular-nums outline-none text-foreground disabled:opacity-50"
        />
      </div>

      <button
        data-testid="list-page-load-btn"
        onClick={submitPage}
        disabled={loading}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Load'}
      </button>

      {hasMore && (
        <button
          data-testid="list-page-next-btn"
          onClick={() => {
            setPageInput(String(page + 1));
            loadPage(page + 1);
          }}
          disabled={loading}
          className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          Next page
          <ChevronRight className="h-3 w-3" />
        </button>
      )}

      {/* Recipient count already sits in the "To" header — don't repeat it here. */}
      <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
        {module} {firstRecord}–{lastRecord}
        {hasMore ? ' · more available' : ' · end of list'}
        {lastSentPage != null && ` · sent p.${lastSentPage}`}
      </span>

      {error && (
        <p data-testid="list-page-error" className="w-full text-[11px] text-amber-600 dark:text-amber-500">
          {error}
        </p>
      )}
    </div>
  );
}
